import LiquidText from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* ============================================================
   REVOLUT BUSINESS — sincronização e reconciliação de despesas
   Transações do cartão importadas da API, casadas com as faturas
   digitalizadas (ATCUD). Mostra o que falta fatura e vice-versa.
   ============================================================ */

const ESTADOS = {
    pendente: { label: 'Sem fatura', bg: '#fee2e2', fg: '#991b1b' },
    conciliada: { label: 'Conciliada', bg: '#dcfce7', fg: '#166534' },
    ignorada: { label: 'Ignorada', bg: '#f1f5f9', fg: '#64748b' },
    // 0,00 €: pré-autorização do comerciante para validar o cartão, sem fatura
    verificacao: { label: 'Verificação de cartão', bg: '#dcfce7', fg: '#166534' },
};

const estadoDaTransacao = (t) =>
    (t.estado_conciliacao === 'pendente' && parseFloat(t.valor || 0) === 0)
        ? ESTADOS.verificacao
        : (ESTADOS[t.estado_conciliacao] || ESTADOS.pendente);

const fmtMoeda = (v) => v == null ? '—' : parseFloat(v).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
const fmtData = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString('pt-PT'); } catch { return '—'; } };
const fmtDataHora = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleString('pt-PT'); } catch { return '—'; } };

/* ---------- Modal de conciliação manual ---------- */
function ConciliarModal({ transacao, onClose, onSaved }) {
    const liquidFeedback = useLiquidFeedback();
    const [sugestoes, setSugestoes] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/revolut/sugestoes/${transacao.id}`)
            .then(r => setSugestoes(r.data || []))
            .catch(() => setSugestoes([]));
    }, [transacao.id]);

    const conciliar = async (despesaId) => {
        setSaving(true);
        try {
            await api.post('/revolut/conciliar', { transacao_id: transacao.id, despesa_id: despesaId });
            onSaved(); onClose();
        } catch (e) { liquidFeedback.notify(e.response?.data?.error || 'Erro ao conciliar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div style={modalHead}>
                    <h3 style={{ margin: 0 }}>Associar fatura à transação</h3>
                    <button onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div data-liquid-surface="true" style={{ background: '#f7fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.88rem' }}>
                    <strong>{transacao.comerciante || transacao.descricao || 'Transação'}</strong> · {fmtMoeda(transacao.valor)} · {fmtDataHora(transacao.data_transacao)}
                    <div style={{ color: '#64748b', fontSize: '0.82rem' }}>
                        {transacao.cartao_nome || (transacao.card_last4 ? `Cartão ****${transacao.card_last4}` : 'Cartão não identificado')}
                    </div>
                </div>
                {sugestoes === null ? <p style={{ color: '#718096' }}>A procurar faturas próximas…</p>
                    : sugestoes.length === 0 ? <p style={{ color: '#94a3b8' }}>Sem faturas por associar em ±10 dias desta data.</p> : (
                        <table className="standard-table" style={{ width: '100%' }}>
                            <thead>
                                <tr><th>Documento</th><th>Fornecedor</th><th>Data</th><th style={{ textAlign: 'right' }}>Valor</th><th></th></tr>
                            </thead>
                            <tbody>
                                {sugestoes.map(d => {
                                    const igual = Math.abs(parseFloat(d.valor_total || 0) - parseFloat(transacao.valor || 0)) <= 0.02;
                                    return (
                                        <tr key={d.id} style={{ background: igual ? '#f0fdf4' : 'transparent' }}>
                                            <td style={{ fontSize: '0.82rem' }}>{d.numero_documento_completo || `#${d.id}`}</td>
                                            <td>{d.fornecedor || '—'}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtData(d.data_documento)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: igual ? 700 : 400, color: igual ? '#166534' : '#334155' }}>{fmtMoeda(d.valor_total)}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button data-liquid-primary="true" disabled={saving} onClick={() => conciliar(d.id)} style={{ ...btnPri, padding: '4px 12px', fontSize: '0.78rem' }}>Associar</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Página ---------- */
const Revolut = ({ embutido = false }) => {
    const liquidFeedback = useLiquidFeedback();
    const navigate = useNavigate();
    const [estado, setEstado] = useState(null);
    const [fotoTx, setFotoTx] = useState(null);   // transação a receber fatura
    const [aEnviar, setAEnviar] = useState(false);
    const fileRef = React.useRef(null);
    const [transacoes, setTransacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);
    const [filtro, setFiltro] = useState('pendente');
    const [conciliar, setConciliar] = useState(null);

    const carregarEstado = useCallback(async () => {
        try {
            const r = await api.get('/revolut/estado', { params: { _t: Date.now() } });
            setEstado(r.data);
        } catch (e) { console.error(e); }
    }, []);

    const carregarTransacoes = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/revolut/transacoes', { params: { estado: filtro, _t: Date.now() } });
            setTransacoes(r.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filtro]);

    useEffect(() => { carregarEstado(); }, [carregarEstado]);
    useEffect(() => { carregarTransacoes(); }, [carregarTransacoes]);

    const autorizar = async () => {
        try {
            const r = await api.get('/revolut/url-autorizacao');
            window.open(r.data.url, '_blank');
        } catch (e) { liquidFeedback.notify(e.response?.data?.error || 'Erro ao obter o link de autorização'); }
    };

    const sincronizar = async (dias = 60) => {
        setSincronizando(true);
        try {
            const r = await api.post('/revolut/sincronizar', { dias }, { timeout: 180000 });
            liquidFeedback.notify(`Sincronização concluída:\n\n• ${r.data.novas} transações novas\n• ${r.data.atualizadas} atualizadas\n• ${r.data.conciliadas} conciliadas automaticamente com faturas`);
            carregarEstado(); carregarTransacoes();
        } catch (e) {
            liquidFeedback.notify('Erro na sincronização: ' + (e.response?.data?.error || e.message));
        } finally { setSincronizando(false); }
    };

    // Anexar foto da fatura diretamente à transação (cria a despesa e concilia)
    const escolherFoto = (t) => { setFotoTx(t); setTimeout(() => fileRef.current?.click(), 0); };

    const enviarFoto = async (e) => {
        const ficheiro = e.target.files?.[0];
        e.target.value = '';
        if (!ficheiro || !fotoTx) return;
        setAEnviar(true);
        try {
            const fd = new FormData();
            fd.append('foto', ficheiro);
            await api.post(`/revolut/${fotoTx.id}/fatura`, fd, { timeout: 120000 });
            carregarTransacoes(); carregarEstado();
        } catch (err) {
            liquidFeedback.notify('Erro ao anexar a fatura: ' + (err.response?.data?.error || err.message));
        } finally { setAEnviar(false); setFotoTx(null); }
    };

    const verFoto = async (t) => {
        try {
            const res = await api.get(`/gestao-financeira/documento/${t.despesa_id}`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(res.data), '_blank');
        } catch { liquidFeedback.notify('Não foi possível abrir a imagem da fatura.'); }
    };

    const acao = async (endpoint, transacao_id) => {
        try {
            await api.post(`/revolut/${endpoint}`, { transacao_id });
            carregarTransacoes(); carregarEstado();
        } catch (e) { liquidFeedback.notify(e.response?.data?.error || 'Erro'); }
    };

    const totalPendente = transacoes
        .filter(t => t.estado_conciliacao === 'pendente')
        .reduce((acc, t) => acc + (parseFloat(t.valor) || 0), 0);

    return (
        <div className={embutido ? '' : 'page-content revolut-page'} style={{ padding: embutido ? 0 : 24 }}>
            {/* input escondido: câmara no telemóvel, ficheiro no desktop */}
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
                onChange={enviarFoto} style={{ display: 'none' }} />

            <div className={embutido ? undefined : 'page-header'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <div>
                    {!embutido && <h1 style={{ margin: 0, fontSize: '1.4rem' }}><OrcamentoIcon name="building" fallback="🏦" /> Revolut Business</h1>}
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>Transações dos cartões e reconciliação com as faturas</p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!embutido && <button onClick={() => navigate('/despesas')} style={btnSec}>← Despesas</button>}
                    {estado?.autorizado && (
                        <>
                            <button data-liquid-primary="true" onClick={() => sincronizar(60)} disabled={sincronizando} style={btnPri}>
                                <LiquidText>{sincronizando ? 'A sincronizar…' : '🔄 Sincronizar (60 dias)'}</LiquidText>
                            </button>
                            <button onClick={() => sincronizar(365)} disabled={sincronizando} style={btnSec} title="Importar o último ano"><OrcamentoIcon name="calendar" fallback="📅" /> Último ano</button>
                        </>
                    )}
                </div>
            </div>

            {/* Estado da ligação */}
            {estado && !estado.autorizado && (
                <div data-liquid-notice="true" data-liquid-tone="warning" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                    <strong style={{ color: '#78350f' }}><OrcamentoIcon name="warning" fallback="⚠️" /> Falta autorizar a aplicação na Revolut</strong>
                    <div style={{ fontSize: '0.86rem', color: '#78350f', margin: '6px 0 10px' }}>
                        Ambiente: <strong>{estado.ambiente}</strong> · Client ID: <LiquidText>{estado.client_id ? '✅' : '❌'}</LiquidText> · Chave privada: <LiquidText>{estado.chave_privada ? '✅' : '❌'}</LiquidText> · Redirect: <LiquidText>{estado.redirect_uri || '❌'}</LiquidText>
                    </div>
                    <button data-liquid-primary="true" onClick={autorizar} disabled={!estado.client_id || !estado.chave_privada} style={btnPri}><LiquidText>
                        🔐 Autorizar na Revolut
                    </LiquidText></button>
                    <div style={{ fontSize: '0.78rem', color: '#92400e', marginTop: 8 }}>
                        Abre o Revolut Business numa nova janela. Depois de confirmar, volta automaticamente e conclui a ligação.
                    </div>
                </div>
            )}

            {estado?.autorizado && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    {/* "Transações" era a soma dos outros três e "Verificações de cartão"
                        são pré-autorizações de 0,00 € sobre as quais não há nada a fazer:
                        dois números que enchiam a linha sem mudar nenhuma decisão. */}
                    {[
                        ['Sem fatura', estado.transacoes?.pendentes || 0, '#fee2e2', '#991b1b'],
                        ['Conciliadas', estado.transacoes?.conciliadas || 0, '#dcfce7', '#166534'],
                    ].map(([l, v, bg, fg]) => (
                        <div key={l} style={{ background: bg, color: fg, borderRadius: 10, padding: '10px 18px', minWidth: 120 }}>
                            <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{v}</div>
                            <div style={{ fontSize: '0.78rem' }}>{l}</div>
                        </div>
                    ))}
                    <div data-liquid-surface="true" style={{ background: '#f8fafc', color: '#475569', borderRadius: 10, padding: '10px 18px', fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                        Última sincronização: <strong style={{ marginLeft: 6 }}>{estado.ultima_sync ? fmtDataHora(estado.ultima_sync) : 'nunca'}</strong>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="shared-subnav revolut-filters" style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                {[['pendente', 'Sem fatura'], ['conciliada', 'Conciliadas'], ['ignorada', 'Ignoradas'], ['todas', 'Todas']].map(([k, l]) => (
                    <button className={`shared-subnav-btn ${filtro === k ? 'active' : ''}`} aria-pressed={filtro === k} key={k} onClick={() => setFiltro(k)}
                        style={{
                            padding: '6px 14px', borderRadius: 16, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer',
                            border: '1px solid ' + (filtro === k ? '#3182ce' : '#cbd5e1'),
                            background: filtro === k ? '#3182ce' : '#fff', color: filtro === k ? '#fff' : '#334155',
                        }}>{l}</button>
                ))}
                {filtro === 'pendente' && transacoes.length > 0 && (
                    <span style={{ marginLeft: 8, fontSize: '0.85rem', color: '#991b1b', fontWeight: 600 }}>
                        {fmtMoeda(totalPendente)} sem fatura associada
                    </span>
                )}
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : transacoes.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        {estado?.autorizado ? 'Sem transações neste filtro. Use "Sincronizar" para importar da Revolut.' : 'Autorize a aplicação para começar a importar transações.'}
                    </div>
                ) : (
                    <div data-liquid-surface="true" className="table-responsive" style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Data</th><th>Comerciante</th><th>Cartão</th>
                                    <th style={{ textAlign: 'right' }}>Valor</th>
                                    <th>Estado</th><th>Fatura associada</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transacoes.map(t => {
                                    const es = estadoDaTransacao(t);
                                    return (
                                        <tr key={t.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtData(t.data_transacao)}</td>
                                            <td>
                                                <strong>{t.comerciante || t.descricao || '—'}</strong>
                                                {t.comerciante_cidade && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{t.comerciante_cidade}</div>}
                                            </td>
                                            <td style={{ fontSize: '0.82rem' }}>
                                                {t.cartao_nome || (t.card_last4 ? `**** ${t.card_last4}` : '—')}
                                                {t.titular_cartao && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t.titular_cartao}</div>}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoeda(t.valor)}</td>
                                            <td><span style={{ ...badge, background: es.bg, color: es.fg }}><LiquidText>{es.label}</LiquidText></span></td>
                                            <td style={{ fontSize: '0.8rem' }}>
                                                {t.despesa_id
                                                    ? <span style={{ color: '#166534' }}>
                                                        {t.despesa_foto
                                                            ? <button onClick={() => verFoto(t)} title="Ver imagem da fatura"
                                                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '1.1rem' }}><LiquidText>🧾</LiquidText></button>
                                                            : <span title="Despesa sem imagem" style={{ opacity: 0.4 }}><OrcamentoIcon name="document" fallback="📄" /></span>}
                                                        {' '}{t.despesa_numero || `#${t.despesa_id}`}<br />
                                                        <span style={{ color: '#94a3b8' }}>{t.despesa_fornecedor} · {fmtMoeda(t.despesa_valor)}</span></span>
                                                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {t.estado_conciliacao === 'conciliada'
                                                    ? (
                                                        <>
                                                            <button onClick={() => escolherFoto(t)} disabled={aEnviar} style={{ ...btnSec, ...mini }} title={t.despesa_foto ? 'Substituir imagem da fatura' : 'Anexar imagem da fatura'}><OrcamentoIcon name="camera" fallback="📷" /></button>
                                                            <button onClick={() => acao('desconciliar', t.id)} style={{ ...btnSec, ...mini }}>Desassociar</button>
                                                        </>
                                                    )
                                                    : (
                                                        <>
                                                            <button data-liquid-primary="true" onClick={() => escolherFoto(t)} disabled={aEnviar} style={{ ...btnPri, ...mini }} title="Tirar/carregar foto da fatura — cria a despesa automaticamente">
                                                                <LiquidText>{aEnviar && fotoTx?.id === t.id ? 'A enviar…' : '📷 Fatura'}</LiquidText>
                                                            </button>
                                                            <button onClick={() => setConciliar(t)} style={{ ...btnSec, ...mini }} title="Associar a uma fatura já registada"><LiquidText>🔗</LiquidText></button>
                                                            {t.estado_conciliacao !== 'ignorada' && (
                                                                <button onClick={() => acao('ignorar', t.id)} style={{ ...btnSec, ...mini }} title="Não gera despesa (transferência, etc.)">Ignorar</button>
                                                            )}
                                                        </>
                                                    )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {conciliar && <ConciliarModal transacao={conciliar} onClose={() => setConciliar(null)} onSaved={() => { carregarTransacoes(); carregarEstado(); }} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 10px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };

export default Revolut;
